/**
 * WalkablePolygon — artist-authored irregular walkable region for painted scenes.
 *
 * Replaces the tile-grid CollisionGrid for painted-world scenes. The polygon is
 * authored over a scene illustration (e.g. with `public/dev/path-editor.html`)
 * as an array of normalized {x, y} points in [0, 1]. The polygon may be
 * concave; vertices wind clockwise or counter-clockwise (either is fine).
 *
 * Coordinate convention: ALL inputs and outputs are normalized scene
 * coordinates in [0, 1] unless otherwise noted. Conversion to/from pixel
 * coordinates is the caller's responsibility (multiply by scene canvas size).
 *
 * Phaser-free by design — point-in-polygon is a 4-line ray cast and we want
 * this importable from Vitest tests without a renderer.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Point3D extends Point {
  z?: number;
}

const EPS = 1e-9;

export class WalkablePolygon {
  readonly points: ReadonlyArray<Point>;

  /** Per-vertex Z elevation (0=foreground, 1=background). Undefined when no Z data present. */
  readonly zValues: ReadonlyArray<number> | undefined;

  /** True when at least one vertex carries a Z value. */
  readonly hasZData: boolean;

  /** Cached axis-aligned bounding box for cheap rejection. */
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;

  constructor(points: ReadonlyArray<Point3D>) {
    if (points.length < 3) {
      throw new Error(`WalkablePolygon needs at least 3 vertices (got ${points.length})`);
    }
    // Defensive copy so external mutation can't corrupt the polygon.
    this.points = points.map(p => ({ x: p.x, y: p.y }));

    const hasAnyZ = points.some(p => p.z !== undefined);
    this.hasZData = hasAnyZ;
    this.zValues = hasAnyZ ? points.map(p => p.z ?? 0) : undefined;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of this.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    this.minX = minX;
    this.minY = minY;
    this.maxX = maxX;
    this.maxY = maxY;
  }

  /** Number of vertices in the polygon ring. */
  get length(): number {
    return this.points.length;
  }

  /**
   * Point-in-polygon test by horizontal ray cast.
   * Boundary points are treated as INSIDE — handy for snap-to-edge behavior.
   */
  contains(x: number, y: number): boolean {
    // Bounding-box reject.
    if (x < this.minX - EPS || x > this.maxX + EPS) return false;
    if (y < this.minY - EPS || y > this.maxY + EPS) return false;

    let inside = false;
    const pts = this.points;
    const n = pts.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = pts[i].x, yi = pts[i].y;
      const xj = pts[j].x, yj = pts[j].y;

      // Boundary check — within EPS of the segment counts as inside.
      if (pointOnSegment(x, y, xi, yi, xj, yj)) return true;

      const intersects = (yi > y) !== (yj > y)
        && x < ((xj - xi) * (y - yi)) / (yj - yi + EPS) + xi;
      if (intersects) inside = !inside;
    }
    return inside;
  }

  /**
   * Find the closest point on the polygon's boundary to (x, y). Always returns
   * a point ON the polygon (vertex or edge projection). Use this to clamp a
   * stray click that landed slightly outside the walkable region.
   */
  closestBoundaryPoint(x: number, y: number): Point {
    let bestX = this.points[0].x;
    let bestY = this.points[0].y;
    let bestD2 = Infinity;

    const pts = this.points;
    const n = pts.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const proj = projectOnSegment(x, y, pts[j].x, pts[j].y, pts[i].x, pts[i].y);
      const dx = proj.x - x;
      const dy = proj.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestX = proj.x;
        bestY = proj.y;
      }
    }
    return { x: bestX, y: bestY };
  }

  /**
   * Return (x, y) if it's already inside the polygon, otherwise the nearest
   * boundary point. Used to clamp click-to-move targets that landed in the sky
   * or off-canvas.
   */
  closestPointInside(x: number, y: number): Point {
    if (this.contains(x, y)) return { x, y };
    const edge = this.closestBoundaryPoint(x, y);
    // Nudge a hair INSIDE so subsequent containment checks succeed.
    const cx = (this.minX + this.maxX) * 0.5;
    const cy = (this.minY + this.maxY) * 0.5;
    const dx = cx - edge.x;
    const dy = cy - edge.y;
    const d = Math.hypot(dx, dy) || 1;
    const nudge = Math.max(EPS * 1000, 1e-4);
    return { x: edge.x + (dx / d) * nudge, y: edge.y + (dy / d) * nudge };
  }

  /**
   * True iff the segment (a → b) lies entirely inside the polygon.
   *
   * Implementation:
   *   1. Both endpoints must be inside (or on boundary).
   *   2. The segment must not properly cross any polygon edge — sharing a
   *      vertex with an edge is fine, but slicing through one is not.
   *   3. The midpoint must be inside — catches the concave-pocket case where
   *      a segment leaves and re-enters the polygon between two vertices
   *      without strictly crossing an edge.
   */
  segmentInside(ax: number, ay: number, bx: number, by: number): boolean {
    if (!this.contains(ax, ay)) return false;
    if (!this.contains(bx, by)) return false;
    if (!this.contains((ax + bx) * 0.5, (ay + by) * 0.5)) return false;

    const pts = this.points;
    const n = pts.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      if (segmentsProperlyCross(ax, ay, bx, by, pts[j].x, pts[j].y, pts[i].x, pts[i].y)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Interpolate Z at an arbitrary (x, y) inside the polygon using inverse-distance
   * weighting from vertices. Returns 0 when no Z data is present.
   */
  interpolateZ(x: number, y: number): number {
    if (!this.zValues) return 0;
    const pts = this.points;
    const zVals = this.zValues;
    let weightSum = 0;
    let zSum = 0;
    for (let i = 0; i < pts.length; i++) {
      const dx = x - pts[i].x;
      const dy = y - pts[i].y;
      const d2 = dx * dx + dy * dy;
      if (d2 < 1e-12) return zVals[i];
      const w = 1 / d2;
      weightSum += w;
      zSum += w * zVals[i];
    }
    return weightSum > 0 ? zSum / weightSum : 0;
  }

  /** Signed area of the polygon via the shoelace formula (normalised coords). */
  estimateArea(): number {
    const pts = this.points;
    const n = pts.length;
    let area = 0;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      area += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
    }
    return Math.abs(area) * 0.5;
  }

  /** Convert a normalized point to pixel coordinates given a canvas size. */
  static toPixel(p: Point, canvasWidth: number, canvasHeight: number): Point {
    return { x: p.x * canvasWidth, y: p.y * canvasHeight };
  }

  /** Convert a pixel point to normalized coordinates given a canvas size. */
  static fromPixel(p: Point, canvasWidth: number, canvasHeight: number): Point {
    return { x: p.x / canvasWidth, y: p.y / canvasHeight };
  }
}

// ── geometry helpers ────────────────────────────────────────────────────────

function pointOnSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq < EPS) {
    return Math.hypot(px - ax, py - ay) < EPS;
  }
  const t = ((px - ax) * dx + (py - ay) * dy) / lengthSq;
  if (t < -EPS || t > 1 + EPS) return false;
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy) < EPS * 1000;
}

function projectOnSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): Point {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq < EPS) return { x: ax, y: ay };
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq));
  return { x: ax + t * dx, y: ay + t * dy };
}

/**
 * Two segments PROPERLY cross iff their interiors intersect at a single point
 * that is not an endpoint of either segment. Shared endpoints don't count —
 * polygon edges share endpoints with their neighbors, and the visibility-graph
 * pathfinder routes between vertices, so we expect endpoint sharing.
 */
function segmentsProperlyCross(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): boolean {
  // Reject when an endpoint is shared (visibility-graph edges meet at vertices).
  if (sameVertex(ax, ay, cx, cy) || sameVertex(ax, ay, dx, dy)) return false;
  if (sameVertex(bx, by, cx, cy) || sameVertex(bx, by, dx, dy)) return false;

  const d1 = cross(dx - cx, dy - cy, ax - cx, ay - cy);
  const d2 = cross(dx - cx, dy - cy, bx - cx, by - cy);
  const d3 = cross(bx - ax, by - ay, cx - ax, cy - ay);
  const d4 = cross(bx - ax, by - ay, dx - ax, dy - ay);

  if (((d1 > EPS && d2 < -EPS) || (d1 < -EPS && d2 > EPS))
    && ((d3 > EPS && d4 < -EPS) || (d3 < -EPS && d4 > EPS))) {
    return true;
  }
  // Collinear / endpoint-on-segment cases are NOT a proper crossing.
  return false;
}

function sameVertex(ax: number, ay: number, bx: number, by: number): boolean {
  return Math.abs(ax - bx) < EPS && Math.abs(ay - by) < EPS;
}

function cross(ax: number, ay: number, bx: number, by: number): number {
  return ax * by - ay * bx;
}
